import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '~/components/ui/button';
import { api } from '~/lib/api-client';
import { proBuilderKeys } from '~/lib/query-keys';
import { reconcileView, terminalReviewCopy, type CheckoutSide } from './pro-builder-checkout-choice';
import { gitProjectPath, type GitActionResult } from './pro-builder-git-actions';
import { useProBuilderPublishStore } from './pro-builder-publish-store';
import { GitVersionsList, ReconcileChoice } from './pro-builder-reconcile-choice';
import type { GitNeedsAction } from './publish-stream';

/**
 * FRONT-3411: the terminal screen's own way out when an app's local files
 * differ from its current version. The open refused with this review; the
 * person picks a side here (the same wording as the Save App dialog) and the
 * terminal opens again in the fresh checkout. Any other answer is shown with
 * a Reconnect, which runs the open (and its checkout setup) again.
 */
export function ProBuilderTerminalCheckout({ review, onReady }: { review: { folder: string } & GitNeedsAction; onReady: () => void }) {
  const qc = useQueryClient();
  const [pending, setPending] = useState(false);
  const [answer, setAnswer] = useState<GitNeedsAction | { error: string } | null>(null);
  const running = useProBuilderPublishStore((s) => s.running.includes(review.folder));
  const busy = pending || running;
  const panelRef = useRef<HTMLDivElement>(null);
  const shown = answer && 'code' in answer ? answer : review;
  const choosing = !!shown.actions?.includes('reconcile');
  const copy = terminalReviewCopy(shown);
  // Without a choice the content proves, nothing is preselected: the heading takes the keyboard instead.
  const headingFocus = choosing && !reconcileView(shown).options.some((option) => option.primary);
  // xterm captures Tab, so the panel takes the keyboard when it appears.
  useEffect(() => {
    panelRef.current?.querySelector<HTMLElement>('[data-pro-builder-autofocus]')?.focus();
  }, [choosing]);

  const choose = async (choice: CheckoutSide) => {
    const store = useProBuilderPublishStore.getState();
    if (store.running.includes(review.folder)) return;
    setPending(true);
    store.setRunning(review.folder, true);
    try {
      const result = await api.post<GitActionResult>(`${gitProjectPath(review.folder)}/reconcile`, { operationId: shown.operationId, choice });
      if (result.status === 'ready') {
        store.setRow(review.folder, null);
        void qc.invalidateQueries({ queryKey: proBuilderKeys.app() });
        void qc.invalidateQueries({ queryKey: proBuilderKeys.projects() });
        void qc.invalidateQueries({ queryKey: ['pro-builder-git-recovery', review.folder] });
        onReady();
      } else if (result.status === 'needs-action') {
        setAnswer(result);
      } else {
        setAnswer({ error: 'This app changed while the terminal was waiting. Press Reconnect to look again.' });
      }
    } catch (error) {
      setAnswer({ error: error instanceof Error ? error.message : 'Could not set up the terminal. Try again.' });
    } finally {
      store.setRunning(review.folder, false);
      setPending(false);
    }
  };

  return (
    <div
      ref={panelRef}
      role="group"
      aria-labelledby="pro-builder-terminal-checkout-title"
      data-testid="pro-builder-terminal-checkout"
      className="absolute inset-0 flex flex-col items-center overflow-auto bg-card/95 p-6"
    >
      <div className="flex w-full max-w-xl flex-col gap-3 text-sm">
        <p id="pro-builder-terminal-checkout-title" className="font-medium text-foreground outline-none" {...(headingFocus ? { tabIndex: -1, 'data-pro-builder-autofocus': '' } : {})}>
          {choosing ? copy.title : "Terminal isn't available right now."}
        </p>
        {choosing
          ? <p className="text-muted-foreground">{copy.lead}</p>
          : <p role="alert" className="text-muted-foreground">{shown.message}</p>}
        {answer && 'error' in answer && <p role="alert" className="text-destructive">{answer.error}</p>}
        {choosing && <ReconcileChoice action={shown} busy={busy} autofocus onChoose={choose} />}
        {busy && <p role="status" className="text-muted-foreground">{pending ? 'Setting up the terminal…' : 'Another draft step is running in Save App. Wait for it to finish.'}</p>}
        {choosing && shown.paths.length > 0 && (
          <details>
            <summary className="cursor-pointer">{copy.filesLabel}</summary>
            <div className="mt-2 flex flex-col gap-2"><GitVersionsList action={shown} /></div>
          </details>
        )}
        {(!choosing || (answer && 'error' in answer)) && (
          <div>
            <Button type="button" variant="outline" size="sm" data-pro-builder-autofocus="" disabled={busy} onClick={onReady}>Reconnect</Button>
          </div>
        )}
      </div>
    </div>
  );
}
